import { drawerOpenWidth } from "./Drawer.jsx";
import {
  Box,
  LinearProgress,
  Toolbar,
  AppBar as MuiAppBar,
  IconButton,
} from "@mui/material";
import React from "react";
import { styled } from "@mui/material/styles";
// import TranslationSwitcher from "./TranslationSwitcher.jsx";
import { useNavigation } from "react-router";
import { LocationSelector } from "./LocationSelector.jsx";
import { UserMenu } from "./UserMenu.jsx";
import ClientSearch from "./ClientSearch.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import * as ws from "../utils/ws.js";

const CustomAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  transition: theme.transitions.create(["width", "left"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        left: drawerOpenWidth,
        width: `calc(100% - ${drawerOpenWidth}px)`,
        transition: theme.transitions.create(["width", "left"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        left: 56,
        width: `calc(100% - ${56}px)`,
        transition: theme.transitions.create(["width", "left"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const SHOW_LOADING_AFTER = 200;
const SHOW_LOADING_AT_LEAST = 1000;

function time() {
  return new Date().getTime();
}

/**
 * This hook allows to show loading indicator whithout glitches when requested
 * - if loading time is fast - no need to show the indicator
 * - if we show the indicator, we shouldn't immediately hide it as it would look like glitch
 *
 * So this hook shows the indicator if loading takes longer than `SHOW_LOADING_AFTER` ms
 * for at least `SHOW_LOADING_AT_LEAST` ms.
 *
 * @param {boolean} show Loading requested?
 *
 * @returns {boolean} Loading should be shown
 */
function useLoading(show) {
  const [showingSince, setShowingSince] = React.useState(0);
  React.useEffect(() => {
    if (show) {
      const timeoutId = setTimeout(() => {
        setShowingSince(time());
      }, SHOW_LOADING_AFTER);

      return function () {
        clearTimeout(timeoutId);
      };
    }

    const currentTime = time();
    const hideIn = Math.max(
      0,
      showingSince + SHOW_LOADING_AT_LEAST - currentTime,
    );
    const timeoutId = setTimeout(() => {
      setShowingSince(0);
    }, hideIn);

    return function () {
      clearTimeout(timeoutId);
    };
  }, [show]);

  return showingSince !== 0;
}

function Loading() {
  const navigation = useNavigation();
  const showLoading = useLoading(navigation.state !== "idle");

  if (!showLoading) {
    return null;
  }

  return (
    <Box>
      <LinearProgress />
    </Box>
  );
}

function getAppVersion() {
  return document
    .querySelector("meta[name=app-version]")
    ?.getAttribute("content");
}

function UpdateButton() {
  const [backendVersion, setBackendVersion] = React.useState(null);
  const frontendVersion = getAppVersion();
  const versionMismatch =
    backendVersion != null && backendVersion != frontendVersion;

  React.useEffect(() => {
    function updateVersion(version) {
      setBackendVersion(version);
    }

    ws.subscribeVersion(updateVersion);

    return function () {
      ws.unsubscribeVersion(updateVersion);
    };
  }, []);

  if (!versionMismatch) {
    return null;
  }

  return (
    <IconButton
      title="Aktualizovat na novou verzi"
      onClick={() => {
        window.location.reload();
      }}
    >
      <FontAwesomeIcon icon={faArrowsRotate} />
    </IconButton>
  );
}

export const AppBar = React.memo(function AppBar({ open }) {
  return (
    <CustomAppBar
      position="fixed"
      open={open}
      sx={{ backgroundColor: "background.default" }}
    >
      <Toolbar sx={{ justifyContent: "end" }}>
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            alignItems: "center",
          }}
        >
          <ClientSearch />
          <Box flexGrow={1} paddingX={2}>
            <UpdateButton />
          </Box>
          <LocationSelector />
          {/*<TranslationSwitcher />*/}
          <UserMenu />
        </Box>
      </Toolbar>
      <Loading />
    </CustomAppBar>
  );
});
